import React, { useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Switch,
  InputNumber,
  Space,
  Card,
  Row,
  Col,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import client from "../../api";
import routes from "src/config/routes";
import { logger } from "src/lib/utils";

interface ICreateCardProduct {
  code: string;
  name: string;
  bank_type_id: number;
  display_order: number;
  benefits: string[];
  cash_out_limit: string | number;
  internal_transfers: string | number;
  card_type_duration: string | number;
  max_cards_per_product: number;
  active: boolean;
}

const CardProductsForm = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const { mutate: createProduct, isLoading } = useMutation(
    client.cardProducts?.add,
    {
      onSuccess: () => navigate(routes.cardProducts),
    },
  );

  // UPDATE
  const { mutate: updatePackage, isLoading: isUpdating } = useMutation(
    client.cardProducts?.update,
    {
      onSuccess: () => navigate(routes.cardProducts),
    },
  );

  const onFinish = (values: ICreateCardProduct) => {
    const payload = {
      ...values,
      card_type_duration: String(values.card_type_duration),
      cash_out_limit: String(values.cash_out_limit),
      internal_transfers: String(values.internal_transfers),
    };

    if (isEdit) {
      updatePackage({
        id,
        ...payload,
      });
    } else {
      createProduct(payload);
    }
  };

  //Product details
  const { mutate: getProduct } = useMutation(client.cardProducts.view, {
    onSuccess: (res: any) => {
      logger.log("Product Details", res);

      const data = res.data;

      form.setFieldsValue({
        code: data.code,
        name: data.name,
        bank_type_id: data.bank_type_id,
        display_order: data.display_order,
        active: data.active,
        benefits:
          data.benefits && data.benefits.length > 0 ? data.benefits : [""],

        cash_out_limit: data.cash_out_limit,
        internal_transfers: data.internal_transfers,
        card_type_duration: data.card_type_duration,
        max_cards_per_product: data.max_cards_per_product,
      });
    },
  });

  useEffect(() => {
    if (isEdit && id) {
      getProduct({ id });
    }
  }, [id, isEdit]);

  return (
    <Card title={isEdit ? "Edit Product" : "Add Product"}>
      {" "}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          active: true,
          benefits: [""],
          display_order: 1,
          max_cards_per_product: 1,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Code"
              name="code"
              rules={[
                {
                  required: true,
                  message: "Please enter code",
                },
              ]}
            >
              <Input placeholder="VIBES" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                {
                  required: true,
                  message: "Please enter name",
                },
              ]}
            >
              <Input placeholder="Fastoo Vibes" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Bank Type ID"
              name="bank_type_id"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Display Order"
              name="display_order"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Cash Out Limit"
              name="cash_out_limit"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Internal Transfers"
              name="internal_transfers"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Card Duration (Months)"
              name="card_type_duration"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Max Cards Per Product"
              name="max_cards_per_product"
              rules={[{ required: true }]}
            >
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Active" name="active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.List name="benefits">
          {(fields, { add, remove }) => (
            <>
              <label className="font-medium mb-3 block">Benefits</label>

              {fields.map(({ key, name }) => (
                <Space
                  key={key}
                  style={{
                    display: "flex",
                    marginBottom: 8,
                  }}
                  align="baseline"
                >
                  <Form.Item
                    name={name}
                    rules={[
                      {
                        required: true,
                        message: "Enter benefit",
                      },
                    ]}
                  >
                    <Input placeholder="Benefit" style={{ width: 500 }} />
                  </Form.Item>

                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}

              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  icon={<PlusOutlined />}
                >
                  Add Benefit
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Space>
          <Button onClick={() => navigate(routes.cardProducts)}>Cancel</Button>

          <Button
            className="bg-green-600 hover:bg-green-700 text-white border-none"
            type="primary"
            htmlType="submit"
            loading={isLoading || isUpdating}
          >
            {isEdit ? "Update Product" : "Save Product"}
          </Button>
        </Space>
      </Form>
    </Card>
  );
};

export default CardProductsForm;
